import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CheckMarkIcon } from '@components/icons';
import { AttentionIcon } from '@components/icons/attention-icon';
import type { HintsProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hints/types';
import type { FaceDetectResult } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { AppTheme } from '@theme';
import { useAppTheme } from '@theme';
import cn from 'classnames';
import Lottie from 'lottie-react';

import { useLivenessErrorText } from '../hooks/use-liveness-error-text';
import { VerificationStatusEnum } from '../types';
import { getHintsPhoto } from '../utils/get-hints-photo';

import animation from './loader.json';
import { HintsContainer } from './styles';

const ERROR_STATUSES = new Set([
    VerificationStatusEnum.FACE_NOT_FOUND,
    VerificationStatusEnum.POOR_LIGHTING,
    VerificationStatusEnum.FILE_ERROR,
]);

const HIDDEN_PHOTO_HINT_STATUSES = new Set([
    VerificationStatusEnum.FACE_NOT_FOUND,
    VerificationStatusEnum.POOR_LIGHTING,
    VerificationStatusEnum.FILE_ERROR,
    VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS,
    VerificationStatusEnum.SUCCESS,
    VerificationStatusEnum.PREPARING,
    VerificationStatusEnum.FACE_DETECTOR_LOADING_SILENT,
]);

const splitHintText = (text: string): { title: string; body?: string } => {
    const match = text.match(/^(.+?[.!?。])(?:\s+([\s\S]+))?$/);

    if (!match?.[2]) {
        return { title: text };
    }

    return { title: match[1].trim(), body: match[2].trim() };
};

const renderErrorHint = (theme: AppTheme, text: string) => {
    const { title, body } = splitHintText(text);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                bgcolor: theme.palette.textMain,
                borderRadius: '20px',
                padding: '6px 12px 8px 12px',
                margin: '0 12px 4px 12px',
                width: 'calc(100% - 24px)',
                maxWidth: '362px',
                boxShadow: `0px 1px 0px rgba(0, 0, 0, 0.06), 0px 9px 10.5px rgba(0, 0, 0, 0.12), inset 0px 0px 1px 0px ${theme.palette.primaryA5}`,
            }}
        >
            <Stack
                sx={{
                    display: 'flex',
                    gap: 0.25,
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Typography
                    tag='p'
                    variant='big-text-bold'
                    color='primary-a7'
                    sx={{ textAlign: 'left', wordBreak: 'break-word' }}
                >
                    {title}
                </Typography>
                {body && (
                    <Typography
                        tag='p'
                        variant='small-text-bold'
                        color='primary-a7'
                        sx={{ textAlign: 'left', wordBreak: 'break-word' }}
                    >
                        {body}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

const SuccessHint: React.FC = () => {
    const theme = useAppTheme();
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                borderRadius: '26px',
                minHeight: '40px',
                mx: 'auto',
                width: '100%',
                maxWidth: '280px',
                color: 'primaryA7',
                bgcolor: 'secondaryA3',
                position: 'relative',
                bottom: '10px',
                boxShadow: `0px 0px 1px 0px ${theme.palette.primaryA5} inset, 0px -6px 44px 0px ${theme.palette.secondaryA3}B2`,

                '@media (max-width: 340px)': {
                    maxWidth: '260px',
                    '& span': {
                        fontSize: '15px',
                    },
                },
            }}
        >
            <CheckMarkIcon />
            <Typography tag='span' variant='big-text-bold' color='primary-a7'>
                {t('docVerificationWidget.liveness.checkCompleted')}
            </Typography>
            {/* Spinner signals the (intentional) post-success wait is a
                hand-off to the next step, not a frozen screen. */}
            <Spinner size={18} style={{ marginLeft: 8 }} />
        </Box>
    );
};

const shouldShowPhotoHint = ({
    faceDetectResult,
    verificationStatus,
    prepareSeconds,
    currentError,
}: {
    faceDetectResult: FaceDetectResult | null;
    verificationStatus: HintsProps['verificationStatus'];
    prepareSeconds: number;
    currentError?: HintsProps['currentError'];
}): boolean => {
    if (!faceDetectResult || prepareSeconds || currentError || !verificationStatus) {
        return false;
    }

    return !HIDDEN_PHOTO_HINT_STATUSES.has(verificationStatus);
};

export const Hints: React.FC<HintsProps> = ({
    faceDetectResult,
    verificationStatus,
    prepareSeconds,
    tryLoadFacemesh,
    errorProceedText,
    currentError,
    fileErrorCode,
    isMaskReady,
    video,
}) => {
    const [videoHeight, setVideoHeight] = useState(0);

    useEffect(() => {
        if (!video) {
            setVideoHeight(0);

            return undefined;
        }

        const updateHeight = () => {
            setVideoHeight(video.clientHeight);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);

        resizeObserver.observe(video);
        video.addEventListener('loadedmetadata', updateHeight);

        return () => {
            resizeObserver.disconnect();
            video.removeEventListener('loadedmetadata', updateHeight);
        };
    }, [video]);

    const theme = useAppTheme();
    const { t } = useTranslation();
    const errorHintText = useLivenessErrorText({
        faceDetectResult,
        verificationStatus,
        currentError,
        fileErrorCode,
        prepareSeconds,
    });

    if (errorProceedText) {
        return null;
    }

    if (verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    margin: 'auto',
                    color: 'secondaryA4',
                    position: 'absolute',
                    top: 'calc(50% - 120px)',
                    zIndex: 1000,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <AttentionIcon />
                    {t('docVerificationWidget.liveness.errorLoadingFaceDetector')}
                </Box>
                <Button
                    variant='primary'
                    onClick={tryLoadFacemesh}
                    style={{ maxWidth: 100, margin: 'auto' }}
                >
                    {t('common.tryAgain')}
                </Button>
            </Box>
        );
    }

    const isError = Boolean(
        verificationStatus && ERROR_STATUSES.has(verificationStatus),
    );

    if (errorHintText) {
        return (
            <HintsContainer
                $videoHeight={videoHeight || undefined}
                className={cn('hints', {
                    'hints--error': isError,
                })}
            >
                {renderErrorHint(theme, errorHintText)}
            </HintsContainer>
        );
    }

    return (
        <HintsContainer $videoHeight={videoHeight || undefined} className='hints'>
            {verificationStatus === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS && (
                <Lottie animationData={animation} loop={true} className='photo-hints' />
            )}
            {verificationStatus === VerificationStatusEnum.SUCCESS && <SuccessHint />}
            {!!prepareSeconds && isMaskReady && (
                <p
                    className='photo-hints'
                    dangerouslySetInnerHTML={{
                        __html: t('docVerificationWidget.liveness.alignFace'),
                    }}
                />
            )}
            {shouldShowPhotoHint({
                faceDetectResult,
                verificationStatus,
                prepareSeconds,
                currentError,
            }) && (
                <p
                    className='photo-hints'
                    dangerouslySetInnerHTML={{
                        __html: t(`${getHintsPhoto(faceDetectResult as FaceDetectResult)}`),
                    }}
                />
            )}
        </HintsContainer>
    );
};
